import { Link } from 'react-router-dom';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../lib/theme/index.js';
import { useUserTypes } from '../hooks/useUserTypes';

export default function Footer() {
  const session = useSession();
  const supabase = useSupabaseClient();
  const { currentTheme, themeVariant, setThemeVariant } = useTheme();
  const { userTypes, isParent, isCampOwner } = useUserTypes();

  // Check admin status using RPC function
  const { data: isAdmin = false } = useQuery(
    ['is-admin', session?.user?.id],
    async () => {
      if (!session?.user?.id) return false;
      
      const { data, error } = await supabase
        .rpc('check_is_admin', {
          user_id: session.user.id
        });
      
      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      
      return !!data;
    },
    {
      enabled: !!session?.user?.id,
      staleTime: 30000, // Cache for 30 seconds
      cacheTime: 60000, // Keep in cache for 1 minute
      retry: false
    }
  );

  // Determine pricing link based on user type
  // If user is only a parent (not a camp owner), go to parent pricing
  // Otherwise, go to owner pricing (default)
  const pricingHref = session && isParent && !isCampOwner 
    ? '/pricing/parents' 
    : '/pricing/owners';

  const footerLinks = [
    { name: 'Pricing', href: pricingHref, public: true },
    { name: 'Camp Diaries', href: '/camp-diaries', public: true },
    { name: 'About', href: '/about', public: true },
    { name: 'Privacy Policy', href: '/privacy', public: true },
    { name: 'Admin', href: '/admin', admin: true }, // Only show for admin users
  ];

  const toggleTheme = () => {
    setThemeVariant(themeVariant === 'purple' ? 'teal' : 'purple');
  };

  return (
    <footer className="bg-white border-t border-gray-200">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-gray-500 text-sm">
            © {new Date().getFullYear()} CampWhiz by Sampa360 LLC. All rights reserved.
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <nav className="flex flex-col sm:flex-row gap-4 sm:gap-6">
              {footerLinks.map((link) => {
                // Skip admin links for non-admin users
                if (link.admin && !isAdmin) return null;
                
                return (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={link.admin 
                      ? "btn-secondary px-3 py-1 text-sm font-medium rounded-md transition-colors"
                      : "text-gray-500 hover:text-gray-700 text-sm transition-colors"
                    }
                  >
                    {link.name}
                  </Link>
                );
              })}
              <button
                onClick={toggleTheme}
                className="text-gray-500 hover:text-gray-700 text-sm transition-colors flex items-center gap-2"
              >
                Theme:
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: currentTheme.primary }}
                ></div>
              </button>
            </nav>

            <div className="flex items-center gap-4 sm:border-l sm:border-gray-200 sm:pl-6">
              <a 
                href="https://www.facebook.com/profile.php?id=61580286057482" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex justify-center p-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                aria-label="Facebook"
              >
                <svg className="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill={currentTheme.primary} viewBox="0 0 24 24">
                  <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z"/>
                </svg>
              </a>
              <a 
                href="https://bsky.app/profile/campwhiz.bsky.social" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex justify-center p-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                aria-label="Bluesky"
              >
                <svg className="w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill={currentTheme.primary} viewBox="0 0 24 24">
                  <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}